import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { useMemo } from 'react';
import React, { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Typography as T } from 'common/typography';

import HierarchyOption from './HierarchyOption';
import { buildHierarchyOptions, searchHierarchy } from './utils';
import { getIdPath } from '../utils';

export type Option = {
  label: string;
  value: string;
  parentId: string | null;
  secondaryContent?: string;
  childrenIds: string[];
};

type Props = {
  allowBranchSelection?: boolean;
  values: string[];
  options: Option[];
  onChange: (value: string, values: string[]) => void;
  search: string;
  caretPosition?: 'left' | 'right';
};

const Text = styled(T.Body3)`
  color: ${cbSemanticCssVars.textSecondary};
`;

const HierarchyOptions = ({
  values,
  onChange,
  options,
  search,
  caretPosition = 'left',
  allowBranchSelection = false,
}: Props) => {
  const isSearching = search !== '';
  const valuesPath = useMemo(() => values.map(value => getIdPath(options, value)), [options, values]);
  const hierarchyOptions = useMemo(() => buildHierarchyOptions(options), [options]);
  const optionsByIdMap = useMemo(
    () =>
      options.reduce((acc, option) => {
        acc.set(option.value, option);
        return acc;
      }, new Map<string, Option>()),
    [options],
  );

  const displayHierarchyOptions: ReactNode[] = useMemo(
    () =>
      hierarchyOptions.reduce<ReactNode[]>((components, hierarchyOption) => {
        if (search !== '' && !searchHierarchy(hierarchyOption, search, optionsByIdMap)) {
          return components;
        }

        return [
          ...components,
          <HierarchyOption
            onChange={onChange}
            initialOpenState={!!isSearching}
            key={hierarchyOption.value}
            values={values}
            valuesPath={valuesPath}
            hierarchyOption={hierarchyOption}
            caretPosition={caretPosition}
            search={search}
            optionsByIdMap={optionsByIdMap}
            allowBranchSelection={allowBranchSelection}
          />,
        ];
      }, []),
    [
      hierarchyOptions,
      isSearching,
      search,
      onChange,
      values,
      valuesPath,
      caretPosition,
      optionsByIdMap,
      allowBranchSelection,
    ],
  );

  if (displayHierarchyOptions.length === 0) {
    return (
      <Text>
        <FormattedMessage id="menu.noResultsFound" />
      </Text>
    );
  }

  return <>{displayHierarchyOptions}</>;
};

export default React.memo(HierarchyOptions);
